/* eslint-disable no-underscore-dangle */
import * as _ from 'lodash';

import type { LocatorAuth } from '../typings/auth';

// output of `toParts()`
export type LocatorParts = {
  fetcher: string | null;
  package: string | null;
  revision: string | null;
};

// consumers can instantiate Locator instances using arbitrary objects as well
//    const loc = new Locator({ fetcher: 'npm', package: 'underscore', revision: '1.2.3' })
type FreeformLocator = Partial<{
  fetcher: string | null;
  package: string | null;
  revision: string | null;
  auth: LocatorAuth;
  auths: LocatorAuth[];
  authIndex: number;
}>

export type LocatorField = {
  resolved: boolean;
  value: string | null;
  full: string | null;
};

// Allowed values to be passed to a setter of a field (fetcher/package/revision)
type LocatorFieldSetValues = LocatorField | string | null;

// Allowed fields for the Locator constructor
// ignore lint error as there's a circular reference, which is OK
// eslint-disable-next-line no-use-before-define
export type LocatorSpec = string | Locator | FreeformLocator;

/**
 * Creates a new Locator instance to describe how to identify, locate and fetch a source package.
 * This class is used heavily throughout the code base as a way to represent the identity of code
 * packages throughout the entirety of both the internet, and our own Database
 * @classdesc This class handles parsing and string generation only with no validation, in the style of url.parse().
 */
export default class Locator {
  // Note: these locator fields will be set in the constructor. We must supply an initial
  // value to satisfy the TypeScript compiler.
  // eslint-disable-next-line @typescript-eslint/naming-convention
  private _fetcher: LocatorField = Locator.getEmptyLocatorField();

  // eslint-disable-next-line @typescript-eslint/naming-convention
  private _package: LocatorField = Locator.getEmptyLocatorField();

  // eslint-disable-next-line @typescript-eslint/naming-convention
  private _revision: LocatorField = Locator.getEmptyLocatorField();

  // Auth can be of many shapes and sizes depending on the fetcher
  public auth?: LocatorAuth | null;

  public auths?: LocatorAuth[];

  public authIndex?: number;

  constructor(locatorSpec: LocatorSpec) {
    if (_.isString(locatorSpec)) {
      // parse into locator string
      // fetcher+package_spec$rev_spec
      // TODO: encode components to escape reserved `+` and `$`
      const locatorPattern = /^(?:([a-z-]+)\+|)([^$]+)(?:\$|)(.+|)$/ig;
      const parseResults = locatorPattern.exec(locatorSpec) || [];
      // default to null to normalize falsy defaults
      this.fetcher = parseResults[1] || null;
      this.package = parseResults[2] || null;
      this.revision = parseResults[3] || null;
    } else if (_.isObject(locatorSpec)) {
      this.fetcher = locatorSpec.fetcher || null;
      this.package = locatorSpec.package || null;
      this.revision = locatorSpec.revision || null;
      this.auth = locatorSpec.auth || null;
      this.auths = locatorSpec.auths || [];
      this.authIndex = locatorSpec.authIndex || 0;
    }
  }

  // Getter methods for locator fields

  get fetcher(): LocatorField {
    return this._fetcher;
  }

  set fetcher(value: LocatorFieldSetValues) {
    this.setLocatorField('_fetcher', value);
  }

  get package(): LocatorField {
    return this._package;
  }

  set package(value: LocatorFieldSetValues) {
    this.setLocatorField('_package', value);
  }

  get revision(): LocatorField {
    return this._revision;
  }

  set revision(value: LocatorFieldSetValues) {
    this.setLocatorField('_revision', value);
  }

  get resolved(): boolean {
    return !!this.fetcher.resolved && !!this.package.resolved && !!this.revision.resolved;
  }

  // Public methods for Locator

  // https://thecodebarbarian.com/what-is-the-tojson-function-in-javascript.html
  // Although not explicitly called in the code, this method is necessary in order
  // to serialize Locator instances, for example if they are included in the body
  // of an API call
  public toJSON(): string {
    return this.toString();
  }

  public toString(): string {
    const fetcher = this.fetcher.value ? `${this.fetcher.value}+` : '';
    const packageField = this.package.value || '';
    const revision = (this.revision.value ? `$${this.revision.value}` : '');

    return fetcher + packageField + revision;
  }

  // retrieves the "full" representation of a Locator. It looks at the Locator field's (fetcher, package, revision)
  // `full` field vs. the `value` field in `toString()`
  public toFullString(): string {
    const fetcher = this.fetcher.value ? `${this.fetcher.full || this.fetcher.value}+` : '';
    const packageField = this.package.full || this.package.value || '';
    const revision = this.revision.value ? `$${this.revision.full || this.revision.value}` : '';

    return fetcher + packageField + revision;
  }

  // retrieves the "package" representation of a Locator. I.e. for a locator of
  // { fetcher: npm, package: underscore, revision: '1.2.3' }, toPackageString() would return `npm+underscore`
  public toPackageString(): string {
    return `${this.fetcher.value ? `${this.fetcher.value}+` : ''}${this.package.value || ''}`;
  }

  public toParts(): LocatorParts {
    return {
      fetcher: this.fetcher.value,
      package: this.package.value,
      revision: this.revision.value,
    };
  }

  // Static methods

  static format(options: LocatorSpec): string {
    return (new Locator(options)).toString();
  }

  static parse(options: LocatorSpec): LocatorParts {
    return (new Locator(options)).toParts();
  }

  static parsePackageString(options: LocatorSpec): string {
    return (new Locator(options)).toPackageString();
  }

  static getEmptyLocatorField(): LocatorField {
    return {
      value: null,
      full: null,
      resolved: false,
    };
  }

  // Private methods for Locator

  // private setter method for all fields of a locator.
  // Any time a consumer updates the fetcher, package, or revision
  // directly, this setter function is called to ensure the correct fields
  // are being set
  private setLocatorField(
    privateField: '_fetcher' | '_package' | '_revision',
    newFieldValue: LocatorFieldSetValues,
  ): void {
    let newValue: LocatorField;
    if (_.isObject(newFieldValue)) {
      newValue = _.pick(newFieldValue, 'value', 'resolved', 'full');
    } else {
      // newFieldValue is a `string | null` here, i.e. a consumer did
      //   locator.package = 'underscore';
      newValue = {
        value: newFieldValue,
        full: newFieldValue,
        // reset resolved to false if field is mutated
        resolved: false,
      };
    }

    this[privateField] = {
      ...Locator.getEmptyLocatorField(),
      // override default values with ones passed in
      ...newValue,
    };
  }

  /**
   * Returns whether the locator is user-defined.
   */
  public isUserDefined(): boolean {
    return this.fetcher.value === 'user';
  }

  /**
   * Checks if the revision associated with the given locator is resolved.
   *
   * @returns {boolean} `true` if the revision is resolved, otherwise `false`
   */
  public isRevisionResolved(): boolean {
    return this.revision.resolved;
  }

  /**
   * Checks if the package associated with the given locator is resolved.
   *
   * @returns {boolean} `true` if the package is resolved, otherwise `false`
   */
  public isPackageResolved(): boolean {
    return this.package.resolved;
  }

  /**
   * Check if this locator represents a project.
   */
  public isProjectLocator(): boolean {
    if (_.isEmpty(this.fetcher.value)) {
      return false;
    }
    if (_.isEmpty(this.package.value)) {
      return false;
    }

    return _.isEmpty(this.revision.value);
  }

  /**
   * Check if this locator represents a revision.
   */
  public isRevisionLocator(): boolean {
    if (_.isEmpty(this.fetcher.value)) {
      return false;
    }
    if (_.isEmpty(this.package.value)) {
      return false;
    }
    if (_.isEmpty(this.revision.value)) {
      return false;
    }

    return true;
  }
}
